import { useContext, memo, useRef, useEffect, useCallback } from "react";
import type { MouseEventHandler } from "react";
import styles from "./StudentPanel.module.css"
import globalStyles from "../global.module.css"
import { HighlightContext, TimePreferencesContext } from "../App";
import { useDrag } from 'react-dnd';
import { completeTimesList } from "../App";

/** Student-Zeile aus mergeStudentsWithSchedule (Table/StudentsInfoPanel). */
export interface PanelStudent {
  member_id: string;
  name?: string | null;
  user_id?: string | null;
  duration?: number | string | null;
  is_set?: boolean;
  is_locked?: boolean;
  is_not_assigned?: boolean;
  set_day?: number | null;
  set_time_from_to?: number[];
  available_times?: unknown;
  available_times_user_input?: unknown;
  split_label?: string | null;
  fullName?: string | null;
  [key: string]: unknown;
}

interface StudentPanelProps {
  student?: PanelStudent | null;
  studentName?: string | null;
  className?: string;
  handleMouseEnter?: MouseEventHandler<HTMLDivElement>;
  handleMouseLeave?: MouseEventHandler<HTMLDivElement>;
  studentMemberId?: string;
}

export default memo(function StudentPanel({student, studentName, className, handleMouseEnter, handleMouseLeave, studentMemberId}: StudentPanelProps) {
    // importing from App.js
    const toggleHighlightCells = useContext(HighlightContext);
    const { timePreferences, onTimePreferenceChange } = useContext(TimePreferencesContext);
    const timePreference = timePreferences?.[student?.member_id as string];
    const splitLabel = student?.split_label;
    const safeStudentName = studentName || "";
    const baseName = splitLabel ? safeStudentName.replace(` (${splitLabel})`, '') : safeStudentName;
    const splitName = baseName.split(" ").filter(Boolean);
    const studentDisplayName = splitName.length > 1 ? `${splitName.at(0)} ${splitName.at(-1)}` : baseName;
    const nameContainerRef = useRef<HTMLDivElement>(null);
    const nameTextRef = useRef<HTMLParagraphElement>(null);

    const handleToggleClick = (newPreference: string) => {
        // Remove current highlights before changing preference
        if (student!.available_times) {
            toggleHighlightCells(student!.available_times, false, globalStyles.highlight);
        }
        if (student!.available_times_user_input) {
            toggleHighlightCells(student!.available_times_user_input, false, globalStyles.highlight);
        }

        // Change the preference
        onTimePreferenceChange(student!.member_id, newPreference);
    }
    
    // useDrag muss vor jeder bedingten Rückgabe verwendet werden
    const [{ isDragging }, drag] = useDrag({
        type: 'STUDENT',
        item: { 
            cellId: student && student.is_set && student.set_day !== null && student.set_time_from_to && student.set_time_from_to.length >= 2
                ? "student_" + student.set_day + "_" + completeTimesList[student.set_time_from_to[0]]
                : "unassigned_student", 
            name: student ? (student.fullName || studentName) : studentName,
            duration: student ? Number(student.duration || 30) / 15 : 2,
            member_id: student ? student.member_id : null,
            user_id: student ? student.user_id : null
        },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
    });

    const updateNameScrollMetrics = useCallback(() => {
        const containerEl = nameContainerRef.current;
        const textEl = nameTextRef.current;

        if (!containerEl || !textEl) {
            return;
        }

        const overflowDistance = Math.max(0, Math.ceil(textEl.scrollWidth - containerEl.clientWidth));
        const isScrollable = overflowDistance > 2;
        const scrollDuration = Math.max(0.35, overflowDistance / 32);

        textEl.style.setProperty('--name-scroll-distance', `${overflowDistance}px`);
        textEl.style.setProperty('--name-scroll-duration', `${scrollDuration}s`);
        textEl.dataset.scrollable = isScrollable ? 'true' : 'false';
    }, []);

    useEffect(() => {
        updateNameScrollMetrics();
        window.addEventListener('resize', updateNameScrollMetrics);

        return () => {
            window.removeEventListener('resize', updateNameScrollMetrics);
        };
    }, [studentDisplayName, updateNameScrollMetrics]);
    
    // Stelle sicher, dass der Student-Prop nicht undefined ist (nach dem useDrag-Hook)
    if (!student) {
        console.error(`Student-Prop für ${studentName} ist undefined!`);
        return (
            <div
                ref={(node) => { drag(node); }}
                className={`${styles.studentPanel} ${styles.errorState}`}
                title="Fehler: Fehlende Studentendaten"
                style={{ height: 'min-content' }}
            >
                <p>{studentName || "Unbekannter Student"}</p>
                <p>(Fehler)</p>
            </div>
        );
    }
    
    // Debug-Ausgabe - ausführlicher für besseres Debugging
    // console.log(`Rendering StudentPanel for ${studentName}:`, { 
    //     is_set: student.is_set, 
    //     set_day: student.set_day, 
    //     set_time_from_to: JSON.stringify(student.set_time_from_to),
    //     // is_not_assigned: student.is_not_assigned,
    //     user_id: student.user_id,
    //     member_id: student.member_id,
    //     fullName: student.fullName
    // });

    // set_time_from_to ist entweder ein Array mit zwei time_ids oder [] wenn Student nicht gesetzt ist
    const set_times = student.set_time_from_to || [];
    
    // Prüfen, ob set_time_from_to ein gültiges Array mit mindestens einem Element ist
    let startTime: string | null = null;
    let endTime: string | null = null;
    
    if (set_times.length >= 2) {
        startTime = completeTimesList[set_times[0]];
        endTime = completeTimesList[set_times[set_times.length - 1]]; // -1 weil set_time_from_to[1] exklusiv ist
        // console.log(`Student ${studentName} hat Zeitbereich: ${startTime} bis ${endTime}`);
    }
    
    // bool to see if student is set or not - Füge zusätzliche Prüfung hinzu
    const isStudentSet = student.is_set && set_times.length >= 2 && student.set_day !== null;
    const durationMult = Number(student.duration)/15;
    // const isNotAssigned = student.is_not_assigned;
    
    // bool to see if student is a registered user
    const isNotRegistered = !student.user_id;

    let titleString = '';
    // if (isNotAssigned) titleString += "Student*in ist nicht zugewiesen";
    // if (isNotAssigned && !student.free_times) titleString += "\n";
    if (!student.available_times_user_input) titleString += "Student*in hat keine freien Zeiten";
    if (isNotRegistered) titleString = "Student*in ist kein registrierter User";
    if (isStudentSet) {
        const dayNames = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
        const dayName = dayNames[student.set_day as number] || `Tag ${student.set_day}`;
        titleString += `\nZugewiesen: ${dayName}, ${startTime} - ${endTime}`;
    }
    
    // Check if both time sources exist
    const hasBothTimeSources = !!(student.available_times && student.available_times_user_input);

    return (
        <div
            ref={(node) => { drag(node); }}
            className={`${styles.studentPanel} 
                      ${isNotRegistered ? styles.notRegistered : ''} 
                      ${isStudentSet ? styles.isSet : styles.isNotSet}
                      ${isDragging ? styles.isDragging : ''}`}
            data-isset={isStudentSet}
            id={student.member_id}
            title={titleString}
            style={{
                height: 'min-content',
                position: 'relative'
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className={styles.studentInfo}>
                <div className={styles.studentNameContainer} ref={nameContainerRef}>
                    <p className={styles.studentNameText} ref={nameTextRef}>
                        {studentDisplayName}
                    </p>
                </div>
                {splitLabel && <span className={styles.splitLabel}>{splitLabel}</span>}
                <p className={styles.studentDuration}>{` (${durationMult*15}')`}</p>
            </div>
            {hasBothTimeSources && (
                <div
                    className={styles.timeToggleButtons}
                    role="group"
                    aria-label="Welche Zeiten für diese Person gelten"
                >
                    <button
                        className={`${styles.toggleButton} ${timePreference === 'available_times' ? styles.active : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleClick('available_times');
                        }}
                        title="Die von dir bearbeiteten Zeiten verwenden"
                        aria-pressed={timePreference === 'available_times'}
                    >
                        Lehrkraft
                    </button>
                    <button
                        className={`${styles.toggleButton} ${timePreference === 'available_times_user_input' ? styles.active : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleClick('available_times_user_input');
                        }}
                        title="Die von der Person selbst eingetragenen Zeiten verwenden"
                        aria-pressed={timePreference === 'available_times_user_input'}
                    >
                        Selbst
                    </button>
                </div>
            )}
            {isStudentSet && (
                <span className={styles.statusIndicator}>✓</span>
            )}
        </div>
    );
});

// TO-DO: Filter wheter to dispaly all/set/not-set Students
// TO-DO: calculate optimum height with 100/sumDurationAllStudents * durationMult + "%"
